import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { resolveEntitlementState, subscriptionRestrictionCopy, type FeatureSummary, type PlanAccess } from '../features/subscription/types';
import { FeatureAvailabilityBadge } from './FeatureAvailabilityBadge';
import { LockedFeatureDialog } from './LockedFeatureDialog';

/**
 * Renders a feature's real, current display state — see `resolveEntitlementState`'s own doc comment for
 * the priority rule. This is a DISPLAY convenience only; the real security boundary is the backend's own
 * `RequireFeatureEntitlementFilter` (`EPN.Identity.Contracts`).
 *
 * States, in the order they win: Coming soon / Pilot only / Not available right now (never a padlock, never an upgrade offer),
 * subscription required (the plan is not the problem), the padlock "Requires <plan>" with a "What changes?" button that opens the
 * `LockedFeatureDialog` (a locked feature is never a dead end or a blank error), and finally the children, with a "Beta" label beside
 * a Beta feature. `showReadinessBadge={false}` is for a list that already labels the feature next to its name.
 */
export function EntitlementGate({
  feature,
  access,
  onUpgradeClick,
  showReadinessBadge = true,
  children,
}: {
  feature: FeatureSummary;
  access: PlanAccess;
  onUpgradeClick?: (feature: FeatureSummary) => void;
  showReadinessBadge?: boolean;
  children: ReactNode;
}) {
  const state = resolveEntitlementState(feature, access);
  const [explaining, setExplaining] = useState(false);

  if (state === 'coming-soon') {
    return (
      <span className="entitlement-badge entitlement-badge--coming-soon" role="status">
        <span aria-hidden="true">◷</span> Coming soon
      </span>
    );
  }

  if (state === 'pilot-only') {
    return <span className="entitlement-badge entitlement-badge--coming-soon" role="status">Pilot only</span>;
  }

  if (state === 'unavailable') {
    return <span className="entitlement-badge entitlement-badge--coming-soon" role="status">Not available right now</span>;
  }

  if (state === 'subscription-required') {
    // The plan itself is not the problem (an upgrade would not help) — the subscription is not currently
    // in force. Say so, and point at the plans screen; never offer "Upgrade".
    const { title } = subscriptionRestrictionCopy(access.effectiveState);
    return (
      <span className="entitlement-locked">
        <span className="entitlement-badge entitlement-badge--locked" role="status">
          <span aria-hidden="true">🔒</span> {title}
        </span>
        <Link className="link entitlement-upgrade-link" to="/settings/subscription">View plans</Link>
      </span>
    );
  }

  if (state === 'locked') {
    const requiredPlanName = feature.minimumPlanName ?? 'a higher plan';
    return (
      <>
        <span className="entitlement-locked">
          <span className="entitlement-badge entitlement-badge--locked" role="status">
            <span aria-hidden="true">🔒</span> Requires {requiredPlanName}
          </span>
          {onUpgradeClick && (
            <button
              type="button"
              className="link entitlement-upgrade-link"
              aria-label={`Upgrade to ${requiredPlanName} to unlock ${feature.displayName}`}
              onClick={() => onUpgradeClick(feature)}
            >
              Upgrade
            </button>
          )}
          <button
            type="button"
            className="link entitlement-upgrade-link"
            aria-haspopup="dialog"
            aria-label={`What changes for ${feature.displayName}`}
            onClick={() => setExplaining(true)}
          >
            What changes?
          </button>
        </span>
        {explaining && (
          <LockedFeatureDialog
            featureName={feature.displayName}
            requiredPlan={feature.minimumPlanName}
            currentPlan={access.planName}
            message={feature.upgradeMessage ?? null}
            onClose={() => setExplaining(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      {children}
      {showReadinessBadge && feature.readinessStatus === 'Beta' && <> <FeatureAvailabilityBadge readinessStatus="Beta" /></>}
    </>
  );
}
